/**
 * PATH: packages/email-hub/src/feature/components/EmailBackgroundControl.tsx
 * FILE_ID: EMAILHUB-BG-CONTROL-001
 * PURPOSE: Popover hosting KanbanBoardBackgroundPicker for Email chrome.
 * WHY:     Same picker UX as Board settings; writes per-user layout_json.background.
 * FLOW:
 *   open → pick → setBackground(next) → EmailLayout applies CSS vars
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Palette } from 'lucide-react'
import { KanbanBoardBackgroundPicker, type BoardConfig } from '@quotech/ui/kanban'
import { cn } from '../../_internal/lib/utils'
import { useUiStore } from '../../_internal/stores/uiStore'
import { hasEmailBackground } from '../../_internal/lib/emailSurfaceBackground'

export function EmailBackgroundControl({
  showLabel = false,
}: {
  showLabel?: boolean
}) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [coords, setCoords] = useState<{ left: number; bottom: number } | null>(null)
  const background = useUiStore((s) => s.background)
  const setBackground = useUiStore((s) => s.setBackground)
  const active = hasEmailBackground(background)

  const recompute = useCallback(() => {
    const el = btnRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const MENU_W = 280
    const left = Math.min(r.left, window.innerWidth - MENU_W - 8)
    const bottom = window.innerHeight - r.top + 6
    setCoords({ left: Math.max(8, left), bottom })
  }, [])

  useEffect(() => {
    if (!open) return
    recompute()
    const onClick = (e: MouseEvent) => {
      if (btnRef.current && btnRef.current.contains(e.target as Node)) return
      const menu = document.getElementById('email-bg-menu')
      if (menu && menu.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onReflow = () => setOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onReflow)
      window.removeEventListener('scroll', onReflow, true)
    }
  }, [open, recompute])

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors',
          showLabel ? 'gap-1.5 px-2 py-1.5 text-xs' : 'justify-center h-8 w-8',
          (open || active) && 'bg-accent text-foreground',
        )}
        title="Change Email workspace background"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="email-background-control"
      >
        <Palette className="w-3.5 h-3.5 shrink-0" />
        {showLabel && <span>Background</span>}
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            id="email-bg-menu"
            role="dialog"
            aria-label="Email background"
            style={{ position: 'fixed', left: coords.left, bottom: coords.bottom, width: 280 }}
            className="z-[100] rounded-lg border border-border bg-popover shadow-xl"
          >
            <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              Workspace background
            </p>
            <KanbanBoardBackgroundPicker
              config={(background ?? {}) as BoardConfig}
              onApply={(next) => {
                setBackground({
                  theme_preset: typeof next.theme_preset === 'string' ? next.theme_preset : undefined,
                  bg_color: typeof next.bg_color === 'string' ? next.bg_color : undefined,
                  bg_image_id: typeof next.bg_image_id === 'string' ? next.bg_image_id : undefined,
                  bg_image_url: typeof next.bg_image_url === 'string' ? next.bg_image_url : undefined,
                })
              }}
            />
          </div>,
          document.body,
        )}
    </>
  )
}
